import React from 'react';
import cn from 'classnames';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import ProgressRing from '@creditflowresearch/uilib/Loader/ProgressRing';
import Button, { type ButtonVariant, type ButtonSize } from '@creditflowresearch/uilib/Button';
import IconFileXls from '@creditflowresearch/uilib/Icon/IconFileXls';

import styles from './exportButtonWithLoader.module.scss';

export interface IProps {
  /**
   * Click handler for the export button.
   */
  onClick: () => void,
  /**
   * Whether the export is currently in progress (shows spinner instead of button).
   */
  isFetching?: boolean,
  /**
   * Whether the generation phase has started (shows progress ring instead of spinner).
   */
  isGenerating?: boolean,
  /**
   * Progress percentage for the generation phase (0–100).
   */
  progress?: number | string,
  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean,
  /**
   * Tooltip text shown when hovering the button.
   */
  tooltipContent?: string,
  /**
   * Tooltip text shown when hovering a disabled button.
   */
  disabledTooltipContent?: string,
  /**
   * Tooltip text shown when hovering the loader (spinner or progress ring).
   */
  loaderTooltipContent?: string,
  /**
   * Status text displayed next to the button during export.
   */
  statusText?: string,
  /**
   * Button variant style.
   */
  variant?: ButtonVariant,
  /**
   * Button size.
   */
  buttonSize?: ButtonSize,
  /**
   * Icon element to render inside the button. Passed directly to Button's `icon` prop.
   */
  icon?: React.ReactNode,
  /**
   * Custom loader component rendered instead of the default spinner/progress ring.
   */
  customLoaderComponent?: React.ReactNode,
  /**
   * data-test attribute for the button.
   */
  dataTest?: string,
  /**
   * Accessible label for the button.
   */
  ariaLabel?: string,
  /**
   * Additional CSS class for the wrapper container.
   */
  className?: string,
  /**
   * Additional CSS class for the button element.
   */
  buttonClassName?: string,
  /**
   * Additional CSS class for the spinner.
   */
  spinnerClassName?: string,
  /**
   * Additional CSS class for the progress ring wrapper.
   */
  loaderClassName?: string,
  /**
   * ProgressRing radius.
   */
  loaderRadius?: number,
  /**
   * ProgressRing stroke width.
   */
  loaderStroke?: number,
}

const ExportButtonWithLoader = ({
  onClick,
  isFetching = false,
  isGenerating = false,
  progress = 0,
  isDisabled = false,
  tooltipContent = 'Export to XLS',
  disabledTooltipContent,
  loaderTooltipContent = 'Loading XLS',
  statusText,
  variant = 'outline',
  buttonSize = 'M',
  icon = <IconFileXls />,
  customLoaderComponent,
  dataTest = 'exportButtonWithLoader',
  ariaLabel = 'Export to XLS',
  className,
  buttonClassName,
  spinnerClassName,
  loaderClassName,
  loaderRadius = 12,
  loaderStroke = 2,
}: IProps): React.ReactElement => {
  const isExporting = isFetching || isGenerating;

  const renderLoader = (): React.ReactNode => {
    if (customLoaderComponent) {
      return customLoaderComponent;
    }

    if (isGenerating) {
      return (
        <Tooltip content={ loaderTooltipContent }>
          <ProgressRing
            radius={ loaderRadius }
            stroke={ loaderStroke }
            progress={ progress }
            wrapperClass={ cn(styles.loader, loaderClassName) }
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip content={ loaderTooltipContent }>
        <Spinner
          size="S"
          inline
          className={ cn(styles.spinner, spinnerClassName) }
        />
      </Tooltip>
    );
  };

  const getTooltipContent = (): string => {
    if (isDisabled && disabledTooltipContent) {
      return disabledTooltipContent;
    }
    return tooltipContent;
  };

  const renderButton = (): React.ReactNode => (
    <Tooltip content={ getTooltipContent() }>
      <Button
        variant={ variant }
        size={ buttonSize }
        icon={ icon }
        disabled={ isDisabled }
        onClick={ onClick }
        className={ cn(styles.button, buttonClassName) }
        dataTest={ dataTest }
        ariaLabel={ ariaLabel }
      />
    </Tooltip>
  );

  return (
    <div className={ cn(styles.container, { [styles.exporting]: isExporting }, className) }>
      { isFetching ? renderLoader() : renderButton() }
      { statusText && (
        <span className={ styles.statusText }>{ statusText }</span>
      ) }
    </div>
  );
};

export default ExportButtonWithLoader;
